import { useEffect, useState } from "react";
import { ArrowUpRight, Check, Clock3, Instagram, Mail, Phone } from "lucide-react";
import axios from "axios";
import { apiClient } from "@/lib/api";
import { DEFAULT_SITE } from "@/data/defaultSite";
import { EditorialMarquee } from "@/components/landing/EditorialMarquee";
import { HeroSection, Header } from "@/components/landing/HeroSection";
import { Reveal } from "@/components/shared/Reveal";

const copy = {
  en: {
    introLabel: "A more considered way to care for home",
    introTitle: "Good landscaping is not loud. It is felt.",
    introBody: "From the first clean edge to the last leaf cleared, Martinez Landscaping brings consistency, care, and a trained eye to the spaces around your home.",
    servicesLabel: "What we do",
    servicesTitle: "The details that make a property feel finished.",
    approachLabel: "Our approach",
    approachTitle: "Four principles. One living landscape.",
    workLabel: "Selected work",
    workTitle: "Places made to be returned to.",
    contactLabel: "Start a conversation",
    contactTitle: "Tell us what your landscape needs next.",
    contactBody: "Share a few details and the Martinez team will follow up about a visit to your Charlotte property.",
    name: "Your name",
    phone: "Phone number",
    email: "Email (optional)",
    service: "What can we help with?",
    message: "A few words about the property",
    send: "Request a visit",
    sent: "Thank you — your request is with the Martinez team.",
    serviceOptions: ["Garden care", "Lawn finish", "Planting design", "Seasonal reset"],
    call: "Prefer to talk? Call",
    hours: "Mon–Sat · 8am–6pm",
    areas: "Serving Charlotte, Mint Hill, Matthews, Indian Trail, and nearby communities.",
    footerLine: "Landscaping with a lasting point of view.",
    privacy: "Your details stay with our team.",
  },
  es: {
    introLabel: "Una forma más atenta de cuidar el hogar",
    introTitle: "El buen paisajismo no grita. Se siente.",
    introBody: "Desde el primer borde limpio hasta la última hoja recogida, Martinez Landscaping aporta constancia, cuidado y una mirada entrenada a los espacios que rodean tu hogar.",
    servicesLabel: "Lo que hacemos",
    servicesTitle: "Los detalles que hacen sentir completa una propiedad.",
    approachLabel: "Nuestro enfoque",
    approachTitle: "Cuatro principios. Un paisaje vivo.",
    workLabel: "Trabajo seleccionado",
    workTitle: "Lugares a los que quieres volver.",
    contactLabel: "Comencemos una conversación",
    contactTitle: "Cuéntanos qué necesita tu paisaje.",
    contactBody: "Comparte algunos detalles y el equipo Martinez te contactará para conversar sobre una visita a tu propiedad en Charlotte.",
    name: "Tu nombre",
    phone: "Número de teléfono",
    email: "Correo electrónico (opcional)",
    service: "¿En qué podemos ayudar?",
    message: "Unas palabras sobre la propiedad",
    send: "Solicitar visita",
    sent: "Gracias — tu solicitud ya está con el equipo Martinez.",
    serviceOptions: ["Cuidado de jardines", "Acabado del césped", "Diseño de plantación", "Renovación estacional"],
    call: "¿Prefieres hablar? Llama al",
    hours: "Lun–Sáb · 8am–6pm",
    areas: "Atendemos Charlotte, Mint Hill, Matthews, Indian Trail y comunidades cercanas.",
    footerLine: "Paisajismo con una visión que perdura.",
    privacy: "Tus datos permanecen con nuestro equipo.",
  },
};

function ServicesSection({ settings, lang }) {
  const text = copy[lang];
  return (
    <section className="section services-section" id="services">
      <Reveal className="section-intro split-heading"><div><span className="eyebrow">{text.servicesLabel}</span><h2>{text.servicesTitle}</h2></div><p>{text.introBody}</p></Reveal>
      <div className="services-list">
        {settings.services.map((service, index) => <Reveal key={service.service_id} className="service-row" style={{ "--reveal-delay": `${index * 80}ms` }}><span className="service-number">{service.number}</span><h3>{lang === "en" ? service.title_en : service.title_es}</h3><p>{lang === "en" ? service.description_en : service.description_es}</p><ArrowUpRight className="service-arrow" size={20} /></Reveal>)}
      </div>
    </section>
  );
}

function ApproachSection({ settings, lang }) {
  const text = copy[lang];
  return (
    <section className="section approach-section" id="approach">
      <Reveal className="section-intro approach-heading"><span className="eyebrow">{text.approachLabel}</span><h2>{text.approachTitle}</h2></Reveal>
      <div className="chapter-list">
        {settings.chapters.map((chapter, index) => <Reveal key={chapter.chapter_id} className={`chapter-row ${index % 2 ? "chapter-reverse" : ""}`} style={{ "--reveal-delay": `${index * 100}ms` }}><div className="chapter-visual"><img src={chapter.image_url} alt={lang === "en" ? chapter.alt_en : chapter.alt_es} data-testid={`manifesto-chapter-image-${chapter.number}`} /><span className="chapter-metric">{lang === "en" ? chapter.metric_en : chapter.metric_es}</span></div><div className="chapter-copy" data-testid={`manifesto-chapter-${chapter.number}`}><div className="chapter-number">{chapter.number}</div><h3>{lang === "en" ? chapter.title_en : chapter.title_es}</h3><p className="chapter-subtitle">{lang === "en" ? chapter.subtitle_en : chapter.subtitle_es}</p><p>{lang === "en" ? chapter.narrative_en : chapter.narrative_es}</p></div></Reveal>)}
      </div>
    </section>
  );
}

function WorkSection({ site, lang }) {
  const text = copy[lang];
  return (
    <section className="section work-section" id="work">
      <Reveal className="section-intro split-heading"><div><span className="eyebrow">{text.workLabel}</span><h2>{text.workTitle}</h2></div><p>{text.areas}</p></Reveal>
      {site.gallery.length ? <div className="portfolio-grid">{site.gallery.map((item, index) => <Reveal key={item.id} className={`portfolio-card portfolio-card-${index + 1}`}><img src={item.image_url} alt={lang === "en" ? item.alt_en : item.alt_es} data-testid={`portfolio-card-image-${item.id}`} /><div className="portfolio-caption"><span>{item.location}</span><h3>{lang === "en" ? item.title_en : item.title_es}</h3></div></Reveal>)}</div> : <div className="empty-gallery-note" data-testid="portfolio-empty-message">{lang === "en" ? "New work will be added here soon." : "Pronto agregaremos nuevos trabajos aquí."}</div>}
    </section>
  );
}

function ContactSection({ settings, lang }) {
  const text = copy[lang];
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: text.serviceOptions[0], message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    try {
      await apiClient.post("/leads", { ...form, email: form.email || null, language: lang });
      setSent(true);
      setForm({ name: "", phone: "", email: "", service: text.serviceOptions[0], message: "" });
    } catch (requestError) {
      setError(typeof requestError.response?.data?.detail === "string" ? requestError.response.data.detail : "Please check your details and try again.");
    }
  };
  return (
    <section className="contact-section" id="contact">
      <div className="contact-copy"><span className="eyebrow">{text.contactLabel}</span><h2>{text.contactTitle}</h2><p>{text.contactBody}</p><div className="contact-meta"><a href={`tel:+1${settings.phones[0].replace(/\D/g, "")}`} data-testid="footer-phone-link"><Phone size={16} />{settings.phones[0]}</a><span><Clock3 size={16} />{text.hours}</span></div></div>
      <form className="contact-form" onSubmit={submit} data-testid="footer-contact-form"><label><span>{text.name}</span><input data-testid="contact-name-input" required value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="Maria Rodriguez" /></label><label><span>{text.phone}</span><input data-testid="contact-phone-input" required value={form.phone} onChange={(event) => update("phone", event.target.value)} placeholder="(980) 000-0000" /></label><label><span>{text.email}</span><input data-testid="contact-email-input" type="email" value={form.email} onChange={(event) => update("email", event.target.value)} placeholder="you@email.com" /></label><label><span>{text.service}</span><select data-testid="contact-service-select" value={form.service} onChange={(event) => update("service", event.target.value)}>{text.serviceOptions.map((option) => <option key={option}>{option}</option>)}</select></label><label className="form-wide"><span>{text.message}</span><textarea data-testid="contact-message-input" required rows="4" value={form.message} onChange={(event) => update("message", event.target.value)} placeholder={lang === "en" ? "Tell us about the yard, garden, or season ahead." : "Cuéntanos sobre el jardín, el patio o la próxima estación."} /></label><div className="form-submit-row"><button className="button button-primary" type="submit" data-testid="contact-submit-button">{text.send}<ArrowUpRight size={17} /></button><span>{text.privacy}</span></div>{sent && <p className="form-status" data-testid="contact-form-status"><Check size={17} />{text.sent}</p>}{error && <p className="form-error" data-testid="contact-form-error">{error}</p>}</form>
    </section>
  );
}

function Footer({ settings, lang }) {
  const text = copy[lang];
  return <footer className="site-footer"><div><span className="footer-kicker">{settings.brand_name}</span><p>{text.footerLine}</p></div><div className="footer-address"><span>{settings.address}</span><span>{settings.location_label}</span></div><div className="footer-links"><a href="mailto:hello@martinezlandscaping.com" data-testid="footer-email-link"><Mail size={15} />Email</a><a href="https://www.instagram.com/" target="_blank" rel="noreferrer" data-testid="footer-instagram-link"><Instagram size={15} />Instagram</a><a href="/admin" data-testid="footer-admin-link">{lang === "en" ? "Admin" : "Administrador"}</a></div></footer>;
}

export function LandingPage() {
  const [site, setSite] = useState(null);
  const [lang, setLang] = useState("en");
  const [heroShift, setHeroShift] = useState(0);
  const [loadError, setLoadError] = useState("");
  useEffect(() => {
    apiClient.get("/site").then((response) => setSite(response.data)).catch(() => { setSite(DEFAULT_SITE); setLoadError("Previewing the saved starting content."); });
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
    if (site) {
      document.title = lang === "en" ? `${site.settings.brand_name} | Charlotte, NC` : `${site.settings.brand_name} | Paisajismo en Charlotte, NC`;
      const description = document.querySelector('meta[name="description"]');
      if (description) description.setAttribute("content", lang === "en" ? site.settings.hero.description_en : site.settings.hero.description_es);
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogTitle) ogTitle.setAttribute("content", `${site.settings.brand_name} | Charlotte, NC`);
      if (ogImage) ogImage.setAttribute("content", site.settings.logo_url);
    }
  }, [lang, site]);
  useEffect(() => {
    if (!site) return;
    const schema = document.querySelector('script[type="application/ld+json"]') || document.createElement("script");
    schema.type = "application/ld+json";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LandscapingBusiness",
      name: site.settings.brand_name,
      image: site.settings.logo_url,
      telephone: site.settings.phones[0],
      address: { "@type": "PostalAddress", streetAddress: site.settings.address, addressLocality: "Charlotte", addressRegion: "NC", postalCode: "28227", addressCountry: "US" },
      areaServed: site.settings.service_areas,
      knowsLanguage: ["en", "es"],
      serviceType: site.settings.services.map((service) => service.title_en),
    });
    if (!schema.parentNode) document.head.appendChild(schema);
  }, [site]);
  useEffect(() => {
    const onScroll = () => setHeroShift(Math.min(window.scrollY * 0.08, 42));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!site) return <div className="loading-screen"><span className="loading-mark">M</span><span>Loading the landscape…</span></div>;
  const text = copy[lang];
  return <div className="site-page"><Header settings={site.settings} lang={lang} onToggle={() => setLang((current) => current === "en" ? "es" : "en")} /><div className="language-announcement" aria-live="polite" data-testid="language-announcement">{lang === "en" ? "English selected" : "Español seleccionado"}</div>{loadError && <div className="preview-notice" data-testid="site-load-notice">{loadError}</div>}<main><HeroSection settings={site.settings} lang={lang} heroShift={heroShift} /><EditorialMarquee items={lang === "en" ? site.settings.marquee_en : site.settings.marquee_es} lang={lang} /><section className="section statement-section"><Reveal><span className="eyebrow">{text.introLabel}</span><h2>{text.introTitle}</h2><p>{text.introBody}</p></Reveal></section><ServicesSection settings={site.settings} lang={lang} /><ApproachSection settings={site.settings} lang={lang} /><WorkSection site={site} lang={lang} /><ContactSection settings={site.settings} lang={lang} /></main><Footer settings={site.settings} lang={lang} /></div>;
}
